import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogOut, Check, X, Mail, Star, Eye, Trash2, Plus, MessageSquare, Users, IndianRupee, Send, FileText, Link2, Loader2, Copy, ExternalLink, RefreshCw, Sparkles, Pencil, AlertTriangle, Clock, Zap, LayoutDashboard, UserCog, Home, Package, Contact, ListTodo, Wand2, Inbox, BookOpen } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import axios from "axios";
import OverviewPanel from "@/components/admin/OverviewPanel";
import TeamPanel from "@/components/admin/TeamPanel";
import OrdersPanel from "@/components/admin/OrdersPanel";
import ClientsPanel from "@/components/admin/ClientsPanel";
import TasksPanel from "@/components/admin/TasksPanel";
import AiCommandPanel from "@/components/admin/AiCommandPanel";
import LeadRoutingPanel from "@/components/admin/LeadRoutingPanel";
import BlogPanel from "@/components/admin/BlogPanel";
import { SkeletonList } from "@/components/admin/Skeleton";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_koko-showcase/artifacts/qadunrd3_1000069989-removebg-preview%20%281%29.png";

function getAuthHeaders() {
  const token = localStorage.getItem("koko_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function AdminDashboard({ portal = "admin" }) {
  const isStaffPortal = portal === "staff";
  const loginPath = isStaffPortal ? "/staff/login" : "/admin/login";
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [reviewsLoaded, setReviewsLoaded] = useState(false);
  const [contactsLoaded, setContactsLoaded] = useState(false);
  const [paymentsLoaded, setPaymentsLoaded] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", rating: 5, message: "" });
  const [addLoading, setAddLoading] = useState(false);
  const [payments, setPayments] = useState([]);
  const [payLinkOpen, setPayLinkOpen] = useState(false);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [payForm, setPayForm] = useState({ client_name: "", client_email: "", client_phone: "", amount: "", description: "", project_name: "" });
  const [invForm, setInvForm] = useState({ client_name: "", client_email: "", client_phone: "", project_name: "", notes: "", items: [{ name: "", amount: "", quantity: 1 }] });
  const [payLoading, setPayLoading] = useState(false);
  const [payResult, setPayResult] = useState(null);
  const [subOpen, setSubOpen] = useState(false);
  const [subForm, setSubForm] = useState({ client_name: "", client_email: "", client_phone: "", project_name: "", amount: "", interval: "monthly", total_count: "12", description: "" });
  const [advOpen, setAdvOpen] = useState(false);
  const [advForm, setAdvForm] = useState({ client_name: "", client_email: "", client_phone: "", project_name: "", description: "", amount: "", project_total: "", due_date: "", payment_terms: "", send_email: true });
  const [advResult, setAdvResult] = useState(null);
  const [balOpen, setBalOpen] = useState(false);
  const [balForm, setBalForm] = useState({ client_name: "", client_email: "", client_phone: "", project_name: "", description: "", project_total: "", advance_paid: "", advance_ref: "", due_date: "", payment_terms: "", send_email: true });
  const [balResult, setBalResult] = useState(null);
  const [recOpen, setRecOpen] = useState(false);
  const [recForm, setRecForm] = useState({ client_name: "", client_email: "", client_phone: "", project_name: "", description: "", amount: "", project_total: "", source_invoice_number: "", payment_method: (typeof localStorage !== "undefined" && localStorage.getItem("koko_last_payment_method")) || "cash", payment_ref: "", payment_date: "", send_email: true });
  const [recResult, setRecResult] = useState(null);
  const [proformas, setProformas] = useState([]);
  const [balances, setBalances] = useState([]);

  const fetchProformas = async () => {
    try {
      const r = await axios.get(`${API}/admin/invoices`, { headers: getAuthHeaders() });
      setProformas((r.data || []).filter((i) => i.kind === "proforma"));
      setBalances((r.data || []).filter((i) => i.kind === "balance"));
    } catch (_) { /* silent */ }
  };
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiRecipientName, setAiRecipientName] = useState("");
  const [aiDraft, setAiDraft] = useState(null);
  const [aiTo, setAiTo] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSending, setAiSending] = useState(false);
  const [aiSent, setAiSent] = useState(false);
  const [aiError, setAiError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const stored = localStorage.getItem("koko_user");
    const token = localStorage.getItem("koko_token");
    if (!stored || !token) { navigate(loginPath); return; }
    // Silent redirect if the user is in the wrong portal for their role
    const gateRole = (role) => {
      if (role === "admin" && isStaffPortal) { navigate("/admin", { replace: true }); return true; }
      if (role !== "admin" && !isStaffPortal) { navigate("/staff", { replace: true }); return true; }
      return false;
    };
    axios.get(`${API}/auth/me`, { headers: getAuthHeaders() })
      .then(r => {
        if (gateRole(r.data?.role)) return;
        setUser(r.data); setLoading(false);
      })
      .catch(async () => {
        // Try refresh if we have a refresh token stored
        const refresh = localStorage.getItem("koko_refresh_token");
        if (refresh) {
          try {
            const { data } = await axios.post(`${API}/auth/refresh`, { refresh_token: refresh });
            if (data.access_token) {
              localStorage.setItem("koko_token", data.access_token);
              const me = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${data.access_token}` } });
              if (gateRole(me.data?.role)) return;
              setUser(me.data); setLoading(false);
              return;
            }
          } catch (_) { /* fall through to logout */ }
        }
        localStorage.removeItem("koko_token");
        localStorage.removeItem("koko_user");
        localStorage.removeItem("koko_refresh_token");
        navigate(loginPath);
      });
  }, [navigate, loginPath, isStaffPortal]);

  useEffect(() => { if (user) { fetchReviews(); fetchContacts(); fetchPayments(); } }, [user]);

  // Keep the currently-active tab centered in the horizontally-scrolling tab
  // rail on mobile. On mount, reset scroll to 0 so the first tab is visible.
  // On every data-state change, center the newly-active tab.
  useEffect(() => {
    if (!user) return;

    const getList = () => document.querySelector('[data-testid="admin-tabs-list"]');

    const centerActive = () => {
      const list = getList();
      if (!list || list.scrollWidth <= list.clientWidth) return;
      const active = list.querySelector('[data-state="active"]');
      if (!active) return;
      // Use bounding rects — offsetLeft/offsetParent can be wrong with
      // flex-nowrap + overflow-x-auto when ancestors have `position`.
      const listRect = list.getBoundingClientRect();
      const activeRect = active.getBoundingClientRect();
      // Button's left edge in scroll-content coordinates:
      const relLeft = (activeRect.left - listRect.left) + list.scrollLeft;
      const target = relLeft - (list.clientWidth - activeRect.width) / 2;
      const max = list.scrollWidth - list.clientWidth;
      list.scrollTo({ left: Math.max(0, Math.min(target, max)), behavior: "smooth" });
    };

    // Reset & center active once tabs are mounted
    const initId = setTimeout(() => {
      const list = getList();
      if (list) list.scrollTo({ left: 0, behavior: "instant" in document.body.style ? "instant" : "auto" });
      centerActive();
    }, 120);

    // Attach observer once list is available
    let obs;
    const attachId = setTimeout(() => {
      const list = getList();
      if (!list) return;
      obs = new MutationObserver(() => {
        // Beat Radix's internal focus-scroll — run after small delay then again to override
        setTimeout(centerActive, 120);
        setTimeout(centerActive, 320);
      });
      for (const btn of list.querySelectorAll('[role="tab"]')) {
        obs.observe(btn, { attributes: true, attributeFilter: ["data-state"] });
      }
    }, 200);

    return () => {
      clearTimeout(initId);
      clearTimeout(attachId);
      if (obs) obs.disconnect();
    };
  }, [user]);

  const fetchReviews = () => { axios.get(`${API}/reviews/all`, { headers: getAuthHeaders() }).then(r => setReviews(r.data)).catch(() => {}).finally(() => setReviewsLoaded(true)); };
  const fetchContacts = () => { axios.get(`${API}/contacts`, { headers: getAuthHeaders() }).then(r => setContacts(r.data)).catch(() => {}).finally(() => setContactsLoaded(true)); };
  const fetchPayments = () => { axios.get(`${API}/admin/payments`, { headers: getAuthHeaders() }).then(r => setPayments(r.data)).catch(() => {}).finally(() => setPaymentsLoaded(true)); fetchProformas(); };
  const approveReview = async (id) => { await axios.put(`${API}/reviews/${id}/approve`, {}, { headers: getAuthHeaders() }); fetchReviews(); };
  const rejectReview = async (id) => { await axios.put(`${API}/reviews/${id}/reject`, {}, { headers: getAuthHeaders() }); fetchReviews(); };
  const deleteReview = async (id) => { if (!window.confirm("Delete this review permanently?")) return; await axios.delete(`${API}/reviews/${id}`, { headers: getAuthHeaders() }); fetchReviews(); };
  const markRead = async (id) => { await axios.put(`${API}/contacts/${id}/read`, {}, { headers: getAuthHeaders() }); fetchContacts(); };
  const deleteContact = async (id) => { await axios.delete(`${API}/contacts/${id}`, { headers: getAuthHeaders() }); fetchContacts(); };

  const handleLogout = () => {
    localStorage.removeItem("koko_token");
    localStorage.removeItem("koko_user");
    localStorage.removeItem("koko_refresh_token");
    navigate(loginPath);
  };

  const handleCreatePayLink = async (e) => {
    e.preventDefault();
    setPayLoading(true);
    setPayResult(null);
    try {
      const { data } = await axios.post(`${API}/admin/payment-link`, { ...payForm, amount: parseInt(payForm.amount) }, { headers: getAuthHeaders() });
      setPayResult(data);
      fetchPayments();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create payment link"); }
    setPayLoading(false);
  };

  const handleCreateInvoice = async (e) => {
    e.preventDefault();
    setPayLoading(true);
    setPayResult(null);
    try {
      const items = invForm.items.filter(i => i.name && i.amount).map(i => ({ ...i, amount: parseInt(i.amount), quantity: parseInt(i.quantity) || 1 }));
      const { data } = await axios.post(`${API}/admin/invoice`, { ...invForm, items }, { headers: getAuthHeaders() });
      setPayResult(data);
      fetchPayments();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create invoice"); }
    setPayLoading(false);
  };

  const addInvItem = () => setInvForm({ ...invForm, items: [...invForm.items, { name: "", amount: "", quantity: 1 }] });
  const updateInvItem = (idx, key, val) => {
    const items = [...invForm.items];
    items[idx][key] = val;
    setInvForm({ ...invForm, items });
  };
  const removeInvItem = (idx) => setInvForm({ ...invForm, items: invForm.items.filter((_, i) => i !== idx) });

  const copyUrl = (url) => { navigator.clipboard.writeText(url); };
  const downloadAdminInvoice = async (razorpayId) => {
    try {
      // Find invoice number for this payment first (admin list)
      const listRes = await axios.get(`${API}/admin/invoices`, { headers: getAuthHeaders() });
      const match = (listRes.data || []).find((inv) => inv.razorpay_id === razorpayId);
      if (!match) {
        toast.error("Invoice not generated yet — hit 'Refresh' to poll Razorpay.");
        return;
      }
      const res = await axios.get(`${API}/admin/invoices/${match.number}/pdf`, {
        headers: getAuthHeaders(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${match.number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (_) {
      toast.error("Failed to download invoice");
    }
  };
  const shareWhatsApp = (payment) => {
    const msg = `Hi ${payment.client_name}, here's your payment link for "${payment.project_name}" (Rs ${payment.amount?.toLocaleString('en-IN')}):\n\n${payment.short_url}\n\n- Kamran, KoKo Sites`;
    window.open(`https://wa.me/${payment.client_phone?.replace(/\D/g, '')}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const handleCreateSubscription = async (e) => {
    e.preventDefault();
    setPayLoading(true);
    setPayResult(null);
    try {
      const { data } = await axios.post(`${API}/admin/subscription`, {
        ...subForm,
        amount: parseInt(subForm.amount),
        total_count: parseInt(subForm.total_count)
      }, { headers: getAuthHeaders() });
      setPayResult(data);
      fetchPayments();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create subscription"); }
    setPayLoading(false);
  };

  const handleCreateAdvance = async (e) => {
    e.preventDefault();
    setPayLoading(true);
    setAdvResult(null);
    try {
      const { data } = await axios.post(`${API}/admin/invoices/proforma`, {
        client_name: advForm.client_name,
        client_email: advForm.client_email,
        client_phone: advForm.client_phone,
        project_name: advForm.project_name,
        description: advForm.description,
        amount: parseFloat(advForm.amount),
        project_total: advForm.project_total ? parseFloat(advForm.project_total) : undefined,
        due_date: advForm.due_date,
        payment_terms: advForm.payment_terms,
        send_email: !!advForm.send_email,
      }, { headers: getAuthHeaders() });
      setAdvResult(data);
      fetchProformas();
      toast.success(`Proforma ${data.number} issued${advForm.send_email ? " + emailed to client" : ""}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create advance invoice"); }
    setPayLoading(false);
  };

  const downloadAdvancePdf = async (number) => {
    try {
      const res = await axios.get(`${API}/admin/invoices/${number}/pdf`, {
        headers: getAuthHeaders(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (_) {
      toast.error("Failed to download PDF");
    }
  };

  const collectOnInvoice = async (inv) => {
    // One-click: create a Razorpay payment link pre-filled with this
    // proforma/balance's details + source reference so that on payment-success
    // the generated final invoice can mark itself PAID or PARTIALLY PAID.
    try {
      const projectTotal =
        inv.kind === "balance"
          ? Number(inv.project_total || 0)
          : Number(inv.project_total || inv.amount || 0);
      const { data } = await axios.post(`${API}/admin/payment-link`, {
        client_name: inv.client_name,
        client_email: inv.client_email,
        client_phone: inv.client_phone || "",
        amount: Math.round(Number(inv.amount || 0)),
        description: inv.description || inv.project_name || `Payment for ${inv.number}`,
        project_name: inv.project_name || "",
        source_invoice_number: inv.number,
        source_invoice_kind: inv.kind,
        project_total: projectTotal,
      }, { headers: getAuthHeaders() });
      // Copy the short_url to clipboard + show a WhatsApp share link.
      if (data.short_url) {
        try { await navigator.clipboard.writeText(data.short_url); } catch (_) {}
        const phoneRaw = (inv.client_phone || "").replace(/\D/g, "");
        const msg = `Hi ${inv.client_name || "there"}, here's the secure payment link for ${inv.number} — Rs ${Number(inv.amount).toLocaleString("en-IN")}. Pay here: ${data.short_url}`;
        const wa = phoneRaw ? `https://wa.me/${phoneRaw}?text=${encodeURIComponent(msg)}` : null;
        if (wa) window.open(wa, "_blank", "noopener,noreferrer");
        toast.success(`Link created for ${inv.number} · copied to clipboard`);
      } else {
        toast.success(`Link created for ${inv.number}`);
      }
      fetchPayments();
      fetchProformas();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create collection link");
    }
  };

  const handleCreateBalance = async (e) => {
    e.preventDefault();
    setPayLoading(true);
    setBalResult(null);
    try {
      const { data } = await axios.post(`${API}/admin/invoices/balance`, {
        client_name: balForm.client_name,
        client_email: balForm.client_email,
        client_phone: balForm.client_phone,
        project_name: balForm.project_name,
        description: balForm.description,
        project_total: parseFloat(balForm.project_total),
        advance_paid: parseFloat(balForm.advance_paid),
        advance_ref: balForm.advance_ref,
        due_date: balForm.due_date,
        payment_terms: balForm.payment_terms,
        send_email: !!balForm.send_email,
      }, { headers: getAuthHeaders() });
      setBalResult(data);
      fetchProformas();
      toast.success(`Balance invoice ${data.number} issued${balForm.send_email ? " + emailed to client" : ""}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create balance invoice"); }
    setPayLoading(false);
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    setPayLoading(true);
    setRecResult(null);
    try {
      const { data } = await axios.post(`${API}/admin/invoices/record-payment`, {
        client_name: recForm.client_name,
        client_email: recForm.client_email,
        client_phone: recForm.client_phone,
        project_name: recForm.project_name,
        description: recForm.description,
        amount: parseFloat(recForm.amount),
        project_total: recForm.project_total ? parseFloat(recForm.project_total) : undefined,
        source_invoice_number: recForm.source_invoice_number || undefined,
        payment_method: recForm.payment_method,
        payment_ref: recForm.payment_ref,
        payment_date: recForm.payment_date,
        send_email: !!recForm.send_email,
      }, { headers: getAuthHeaders() });
      // Remember this method so the dropdown defaults to it next time.
      try { localStorage.setItem("koko_last_payment_method", recForm.payment_method); } catch (_) { /* ignore */ }
      setRecResult(data);
      fetchPayments();
      fetchProformas();
      toast.success(`Invoice ${data.number} created${data.partial_payment ? " (PARTIALLY PAID)" : ""}${recForm.send_email ? " + emailed" : ""}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to record payment"); }
    setPayLoading(false);
  };

  const openRecordForInvoice = (inv) => {
    // Pre-fill the Record Payment modal from a proforma/balance row.
    setRecForm({
      client_name: inv.client_name || "",
      client_email: inv.client_email || "",
      client_phone: inv.client_phone || "",
      project_name: inv.project_name || "",
      description: inv.description || "",
      amount: String(inv.amount || ""),
      project_total: String(inv.kind === "balance"
        ? (inv.project_total || "")
        : (inv.project_total || "")),
      source_invoice_number: inv.number || "",
      payment_method: localStorage.getItem("koko_last_payment_method") || "cash",
      payment_ref: "",
      payment_date: "",
      send_email: true,
    });
    setRecOpen(true);
  };

  const handleAiDraft = async () => {
    if (!aiPrompt.trim()) return;
    setAiLoading(true);
    setAiDraft(null);
    setAiSent(false);
    try {
      const { data } = await axios.post(`${API}/admin/ai-email-draft`, {
        prompt: aiPrompt,
        recipient_name: aiRecipientName,
      }, { headers: getAuthHeaders() });
      setAiDraft(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to generate draft"); }
    setAiLoading(false);
  };

  const handleAiSend = async () => {
    if (!aiTo.trim() || !aiDraft) return;
    setAiSending(true);
    setAiError(null);
    try {
      const htmlBody = aiDraft.body.replace(/\n/g, '<br/>').split('<br/>').map(line =>
        `<p style="color:#ccc;font-size:14px;line-height:1.6;margin:0 0 8px;">${line || '&nbsp;'}</p>`
      ).join('');
      await axios.post(`${API}/admin/send-email`, {
        to: aiTo,
        subject: aiDraft.subject,
        html_body: htmlBody,
      }, { headers: getAuthHeaders() });
      setAiSent(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code) {
        setAiError(detail);
      } else {
        setAiError({ code: "send_failed", message: detail || err.message || "Failed to send email. Please try again." });
      }
    }
    setAiSending(false);
  };

  const resetAiEmail = () => {
    setAiPrompt(""); setAiRecipientName(""); setAiDraft(null);
    setAiTo(""); setAiSent(false); setAiError(null);
  };

  const handleAddReview = async (e) => {
    e.preventDefault();
    setAddLoading(true);
    try {
      await axios.post(`${API}/reviews/admin`, addForm, { headers: getAuthHeaders() });
      setAddForm({ name: "", rating: 5, message: "" });
      setAddOpen(false);
      fetchReviews();
    } catch (err) { /* silently fail */ }
    setAddLoading(false);
  };

  if (loading) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><div className="w-8 h-8 border-2 border-red-600 border-t-transparent animate-spin" /></div>;

  const pendingReviews = reviews.filter(r => r.status === "pending");
  const unreadContacts = contacts.filter(c => !c.read);
  const totalRevenue = payments.filter(p => p.status === "paid").reduce((sum, p) => sum + (p.amount || 0), 0);
  const paidCount = payments.filter(p => p.status === "paid").length;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#0A0A0A]">
      <header className="border-b border-white/10 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/"><img src={LOGO_URL} alt="KoKo Sites" className="h-8" /></Link>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">{isStaffPortal ? "Staff" : "Admin"}</span>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <Link
              to="/"
              data-testid="admin-back-to-site"
              className="hidden sm:inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.15em] text-zinc-400 hover:text-white border border-white/15 hover:border-white/40 px-3 py-2 transition-colors whitespace-nowrap"
              title="View public site"
            >
              <Home size={12} /> Back to Site
            </Link>
            <Link
              to="/"
              data-testid="admin-back-to-site-mobile"
              className="sm:hidden inline-flex items-center justify-center w-9 h-9 text-zinc-400 hover:text-white border border-white/15 hover:border-white/40 transition-colors"
              title="View public site"
              aria-label="Back to site"
            >
              <Home size={16} />
            </Link>
            <Link
              to={isStaffPortal ? "/staff/profile" : "/admin/profile"}
              data-testid="admin-profile-link"
              className="flex items-center gap-2.5 group hover:bg-white/5 pl-1 pr-2 sm:pr-3 py-1 transition-colors"
              title="Edit profile"
            >
              <div className="w-8 h-8 border border-white/10 bg-black/40 flex items-center justify-center overflow-hidden">
                {user?.avatar_url ? (
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${user.avatar_url}`} alt="me" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-xs font-black text-white">{(user?.display_name || user?.name || "?").slice(0, 1).toUpperCase()}</span>
                )}
              </div>
              <span className="text-xs text-zinc-300 hidden md:block group-hover:text-white">{user?.display_name || user?.name}</span>
            </Link>
            <button onClick={handleLogout} data-testid="admin-logout" className="text-zinc-400 hover:text-red-500 transition-colors p-2"><LogOut size={18} /></button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList data-testid="admin-tabs-list" className="bg-[#141414] border border-white/10 rounded-none p-1 flex flex-nowrap !justify-start overflow-x-auto lg:flex-wrap lg:h-auto no-scrollbar scroll-smooth">
            <TabsTrigger value="overview" data-testid="admin-overview-tab" className="shrink-0 whitespace-nowrap rounded-none data-[state=active]:bg-red-600 data-[state=active]:text-white text-xs uppercase tracking-wider font-bold">
              <LayoutDashboard size={14} className="mr-2" /> Overview
            </TabsTrigger>
            {user?.permissions?.reviews !== false && (
              <TabsTrigger value="reviews" data-testid="admin-reviews-tab" className="shrink-0 whitespace-nowrap rounded-none data-[state=active]:bg-red-600 data-[state=active]:text-white text-xs uppercase tracking-wider font-bold">
                <Users size={14} className="mr-2" /> Reviews ({reviews.length})
              </TabsTrigger>
            )}
            {user?.permissions?.contacts !== false && (
              <TabsTrigger value="contacts" data-testid="admin-contacts-tab" className="shrink-0 whitespace-nowrap rounded-none data-[state=active]:bg-red-600 data-[state=active]:text-white text-xs uppercase tracking-wider font-bold">
                <MessageSquare size={14} className="mr-2" /> Messages ({contacts.length})
              </TabsTrigger>
            )}
            {user?.permissions?.payments !== false && (
              <TabsTrigger value="payments" data-testid="admin-payments-tab" className="shrink-0 whitespace-nowrap rounded-none data-[state=active]:bg-red-600 data-[state=active]:text-white text-xs uppercase tracking-wider font-bold">
                <IndianRupee size={14} className="mr-2" /> Payments ({payments.length})
              </TabsTrigger>
            )}
            {user?.permissions?.orders !== false && (
              <TabsTrigger value="orders" data-testid="admin-orders-tab" className="shrink-0 whitespace-nowrap rounded-none data-[state=active]:bg-red-600 data-[state=active]:text-white text-xs uppercase tracking-wider font-bold">
                <Package size={14} className="mr-2" /> Orders
              </TabsTrigger>
            )}
            {user?.permissions?.clients !== false && (
              <TabsTrigger value="clients" data-testid="admin-clients-tab" className="shrink-0 whitespace-nowrap rounded-none data-[state=active]:bg-red-600 data-[state=active]:text-white text-xs uppercase tracking-wider font-bold">
                <Contact size={14} className="mr-2" /> Clients
              </TabsTrigger>
            )}
            {user?.permissions?.tasks !== false && (
              <TabsTrigger value="tasks" data-testid="admin-tasks-tab" className="shrink-0 whitespace-nowrap rounded-none data-[state=active]:bg-red-600 data-[state=active]:text-white text-xs uppercase tracking-wider font-bold">
                <ListTodo size={14} className="mr-2" /> Tasks
              </TabsTrigger>
            )}
            {user?.permissions?.ai_command !== false && (
              <TabsTrigger value="ai-command" data-testid="admin-ai-command-tab" className="shrink-0 whitespace-nowrap rounded-none data-[state=active]:bg-red-600 data-[state=active]:text-white text-xs uppercase tracking-wider font-bold">
                <Wand2 size={14} className="mr-2" /> AI Command
              </TabsTrigger>
            )}
            {user?.permissions?.ai_email !== false && (
              <TabsTrigger value="ai-email" data-testid="admin-ai-email-tab" className="shrink-0 whitespace-nowrap rounded-none data-[state=active]:bg-red-600 data-[state=active]:text-white text-xs uppercase tracking-wider font-bold">
                <Sparkles size={14} className="mr-2" /> AI Email
              </TabsTrigger>
            )}
            {user?.permissions?.email_health !== false && (
              <TabsTrigger value="email-health" data-testid="admin-email-health-tab" className="shrink-0 whitespace-nowrap rounded-none data-[state=active]:bg-red-600 data-[state=active]:text-white text-xs uppercase tracking-wider font-bold">
                <Mail size={14} className="mr-2" /> Email Health
              </TabsTrigger>
            )}
            {user?.role === "admin" && (
              <TabsTrigger value="lead-routing" data-testid="admin-lead-routing-tab" className="shrink-0 whitespace-nowrap rounded-none data-[state=active]:bg-red-600 data-[state=active]:text-white text-xs uppercase tracking-wider font-bold">
                <Inbox size={14} className="mr-2" /> Lead Routing
              </TabsTrigger>
            )}
            {user?.role === "admin" && (
              <TabsTrigger value="blog" data-testid="admin-blog-tab" className="shrink-0 whitespace-nowrap rounded-none data-[state=active]:bg-red-600 data-[state=active]:text-white text-xs uppercase tracking-wider font-bold">
                <BookOpen size={14} className="mr-2" /> Blog
              </TabsTrigger>
            )}
            {user?.role === "admin" && (
              <TabsTrigger value="team" data-testid="admin-team-tab" className="shrink-0 whitespace-nowrap rounded-none data-[state=active]:bg-red-600 data-[state=active]:text-white text-xs uppercase tracking-wider font-bold">
                <UserCog size={14} className="mr-2" /> Team
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="overview">
            <OverviewPanel API={API} token={localStorage.getItem("koko_token")} />
          </TabsContent>

          <TabsContent value="orders">
            <OrdersPanel API={API} getAuthHeaders={getAuthHeaders} />
          </TabsContent>

          <TabsContent value="clients">
            <ClientsPanel API={API} getAuthHeaders={getAuthHeaders} />
          </TabsContent>

          <TabsContent value="tasks">
            <TasksPanel API={API} getAuthHeaders={getAuthHeaders} currentUser={user} />
          </TabsContent>

          <TabsContent value="ai-command">
            <AiCommandPanel API={API} getAuthHeaders={getAuthHeaders} currentUser={user} />
          </TabsContent>

          <TabsContent value="lead-routing">
            <LeadRoutingPanel API={API} getAuthHeaders={getAuthHeaders} />
          </TabsContent>

          <TabsContent value="blog">
            <BlogPanel API={API} getAuthHeaders={getAuthHeaders} />
          </TabsContent>

          <TabsContent value="reviews" className="space-y-4">
            <div className="flex justify-end">
              <Dialog open={addOpen} onOpenChange={setAddOpen}>
                <DialogTrigger asChild>
                  <button data-testid="add-review-btn" className="bg-red-600 text-white text-xs font-bold uppercase tracking-widest py-2 px-6 flex items-center gap-2 hover:bg-white hover:text-black transition-colors">
                    <Plus size={14} /> Add Review
                  </button>
                </DialogTrigger>
                <DialogContent className="bg-[#141414] border border-white/10 rounded-none text-white">
                  <DialogHeader><DialogTitle className="text-sm font-bold uppercase tracking-wider">Add Review (Auto-Approved)</DialogTitle></DialogHeader>
                  <form onSubmit={handleAddReview} className="space-y-4 mt-4">
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-1 block">Reviewer Name</Label>
                      <Input data-testid="add-review-name" value={addForm.name} onChange={e => setAddForm({...addForm, name: e.target.value})} required placeholder="Name" className="bg-transparent border-white/20 rounded-none text-white focus-visible:ring-0" />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-1 block">Rating</Label>
                      <div className="flex gap-2" data-testid="add-review-rating">
                        {[1,2,3,4,5].map(n => (
                          <button key={n} type="button" onClick={() => setAddForm({...addForm, rating: n})}>
                            <Star size={20} className={n <= addForm.rating ? "text-red-500 fill-red-500" : "text-zinc-600"} />
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-1 block">Review Message</Label>
                      <Textarea data-testid="add-review-message" value={addForm.message} onChange={e => setAddForm({...addForm, message: e.target.value})} required rows={3} placeholder="Write review..." className="bg-transparent border-white/20 rounded-none text-white resize-none focus-visible:ring-0" />
                    </div>
                    <button type="submit" disabled={addLoading} data-testid="add-review-submit" className="w-full bg-red-600 text-white py-3 text-xs font-bold uppercase tracking-widest hover:bg-white hover:text-black transition-colors disabled:opacity-50">
                      {addLoading ? "Adding..." : "Add Review"}
                    </button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {reviews.length === 0 && reviewsLoaded && <p className="text-zinc-500 text-sm text-center py-12">No reviews yet.</p>}
            {!reviewsLoaded && <SkeletonList count={3} />}
            {reviews.map((r) => (
              <div key={r.id} data-testid={`admin-review-${r.id}`} className="border border-white/10 bg-[#141414] p-6 flex flex-col sm:flex-row sm:items-start gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="font-bold text-white text-sm uppercase tracking-wider">{r.name}</span>
                    <Badge className={`rounded-none text-[10px] uppercase tracking-wider ${r.status === "approved" ? "bg-green-600/10 text-green-400 border-green-600/20" : r.status === "rejected" ? "bg-red-600/10 text-red-400 border-red-600/20" : "bg-yellow-600/10 text-yellow-400 border-yellow-600/20"}`} variant="outline">{r.status}</Badge>
                  </div>
                  <div className="flex gap-1 mb-2">{[...Array(r.rating || 5)].map((_, j) => <Star key={j} size={12} className="text-red-500 fill-red-500" />)}</div>
                  <p className="text-sm text-zinc-400 mb-1">{r.message}</p>
                  <p className="text-xs text-zinc-600">{r.email && `${r.email} · `}{new Date(r.created_at).toLocaleDateString('en-IN')}</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  {r.status === "pending" && (
                    <>
                      <button onClick={() => approveReview(r.id)} data-testid={`approve-review-${r.id}`} className="bg-green-600 text-white p-2 hover:bg-green-700 transition-colors" title="Approve"><Check size={16} /></button>
                      <button onClick={() => rejectReview(r.id)} data-testid={`reject-review-${r.id}`} className="bg-red-600 text-white p-2 hover:bg-red-700 transition-colors" title="Reject"><X size={16} /></button>
                    </>
                  )}
                  <button onClick={() => deleteReview(r.id)} data-testid={`delete-review-${r.id}`} className="bg-[#0A0A0A] border border-white/10 text-zinc-400 p-2 hover:text-red-500 hover:border-red-600/30 transition-colors" title="Delete"><Trash2 size={16} /></button>
                </div>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="contacts" className="space-y-4">
            {contacts.length === 0 && contactsLoaded && <p className="text-zinc-500 text-sm text-center py-12">No messages yet.</p>}
            {!contactsLoaded && <SkeletonList count={3} />}
            {contacts.map((c) => (
              <div key={c.id} data-testid={`admin-contact-${c.id}`} className={`border bg-[#141414] p-6 ${c.read ? 'border-white/10 opacity-60' : 'border-red-600/30'}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-bold text-white text-sm uppercase tracking-wider">{c.name}</span>
                      {!c.read && <Badge className="rounded-none text-[10px] uppercase tracking-wider bg-red-600/10 text-red-400 border-red-600/20" variant="outline">New</Badge>}
                    </div>
                    {c.subject && <p className="text-sm text-white mb-1">{c.subject}</p>}
                    <p className="text-sm text-zinc-400 mb-2">{c.message}</p>
                    <div className="flex flex-wrap gap-4 text-xs text-zinc-500">
                      <span className="flex items-center gap-1"><Mail size={10} /> {c.email}</span>
                      {c.phone && <span>Tel: {c.phone}</span>}
                      <span>{new Date(c.created_at).toLocaleDateString('en-IN')}</span>
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    {!c.read && <button onClick={() => markRead(c.id)} data-testid={`mark-read-${c.id}`} className="bg-[#0A0A0A] border border-white/10 text-zinc-400 p-2 hover:text-white transition-colors" title="Mark as read"><Eye size={14} /></button>}
                    <button onClick={() => deleteContact(c.id)} data-testid={`delete-contact-${c.id}`} className="bg-[#0A0A0A] border border-white/10 text-zinc-400 p-2 hover:text-red-500 transition-colors" title="Delete"><Trash2 size={14} /></button>
                  </div>
                </div>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="payments" className="space-y-6">
            {/* Action buttons */}
            <div className="flex flex-wrap gap-3">
              <Dialog open={payLinkOpen} onOpenChange={(v) => { setPayLinkOpen(v); if (!v) setPayResult(null); }}>
                <DialogTrigger asChild>
                  <button data-testid="create-payment-link-btn" className="bg-red-600 text-white text-xs font-bold uppercase tracking-widest py-2 px-6 flex items-center gap-2 hover:bg-white hover:text-black transition-colors">
                    <Link2 size={14} /> Payment Link
                  </button>
                </DialogTrigger>
                <DialogContent className="bg-[#141414] border border-white/10 rounded-none text-white max-w-lg max-h-[85vh] overflow-y-auto">
                  <DialogHeader><DialogTitle className="text-sm font-bold uppercase tracking-wider">Create Payment Link</DialogTitle></DialogHeader>
                  {payResult ? (
                    <div className="space-y-4 mt-4">
                      <div className="border border-green-600/30 bg-green-600/5 p-4 text-center">
                        <Check size={24} className="text-green-500 mx-auto mb-2" />
                        <p className="text-sm font-bold text-green-400 mb-1">Payment Link Created!</p>
                        <p className="text-2xl font-black text-white">Rs {payResult.amount?.toLocaleString('en-IN')}</p>
                      </div>
                      <div className="flex items-center gap-2 border border-white/10 p-3">
                        <input readOnly value={payResult.short_url} className="flex-1 bg-transparent text-xs text-zinc-300 outline-none" />
                        <button onClick={() => copyUrl(payResult.short_url)} className="text-zinc-400 hover:text-white"><Copy size={14} /></button>
                        <a href={payResult.short_url} target="_blank" rel="noreferrer" className="text-zinc-400 hover:text-white"><ExternalLink size={14} /></a>
                      </div>
                      <button onClick={() => shareWhatsApp({ ...payForm, amount: parseInt(payForm.amount), short_url: payResult.short_url })} className="w-full py-3 bg-[#25D366] text-white font-bold uppercase tracking-widest text-xs flex items-center justify-center gap-2 hover:brightness-110">
                        <Send size={14} /> Share on WhatsApp
                      </button>
                      <button onClick={() => { setPayResult(null); setPayForm({ client_name: "", client_email: "", client_phone: "", amount: "", description: "", project_name: "" }); }} className="w-full text-xs text-zinc-500 hover:text-white text-center py-2">Create Another</button>
                    </div>
                  ) : (
                    <form onSubmit={handleCreatePayLink} className="space-y-3 mt-4">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Client Name *</Label>
                          <Input data-testid="paylink-name" value={payForm.client_name} onChange={e => setPayForm({...payForm, client_name: e.target.value})} required placeholder="Rahul Sharma" className="bg-transparent border-white/20 rounded-none text-white text-sm focus-visible:ring-0" />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Phone *</Label>
                          <Input data-testid="paylink-phone" value={payForm.client_phone} onChange={e => setPayForm({...payForm, client_phone: e.target.value})} required type="tel" inputMode="tel" autoComplete="tel" placeholder="+91 98765 43210" className="bg-transparent border-white/20 rounded-none text-white text-sm focus-visible:ring-0" />
                        </div>
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Email *</Label>
                        <Input data-testid="paylink-email" value={payForm.client_email} onChange={e => setPayForm({...payForm, client_email: e.target.value})} required type="email" placeholder="rahul@email.com" className="bg-transparent border-white/20 rounded-none text-white text-sm focus-visible:ring-0" />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Project Name *</Label>
                          <Input value={payForm.project_name} onChange={e => setPayForm({...payForm, project_name: e.target.value})} required placeholder="Singh Dental Website" className="bg-transparent border-white/20 rounded-none text-white text-sm focus-visible:ring-0" />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Amount (Rs) *</Label>
                          <Input data-testid="paylink-amount" value={payForm.amount} onChange={e => setPayForm({...payForm, amount: e.target.value})} required type="number" min="1" placeholder="15000" className="bg-transparent border-white/20 rounded-none text-white text-sm focus-visible:ring-0" />
                        </div>
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Description</Label>
                        <Input value={payForm.description} onChange={e => setPayForm({...payForm, description: e.target.value})} placeholder="Website design - 5 pages + animations" className="bg-transparent border-white/20 rounded-none text-white text-sm focus-visible:ring-0" />
                      </div>
                      <button type="submit" disabled={payLoading} data-testid="paylink-submit" className="w-full bg-red-600 text-white py-3 text-xs font-bold uppercase tracking-widest hover:bg-white hover:text-black transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                        {payLoading ? <><Loader2 size={14} className="animate-spin" /> Creating...</> : <><Link2 size={14} /> Create Payment Link</>}
                      </button>
                    </form>
                  )}
                </DialogContent>
              </Dialog>

              <Dialog open={invoiceOpen} onOpenChange={(v) => { setInvoiceOpen(v); if (!v) setPayResult(null); }}>
                <DialogTrigger asChild>
                  <button data-testid="create-invoice-btn" className="bg-[#141414] border border-white/10 text-white text-xs font-bold uppercase tracking-widest py-2 px-6 flex items-center gap-2 hover:border-red-600/50 hover:text-red-500 transition-colors">
                    <FileText size={14} /> Invoice
                  </button>
                </DialogTrigger>
                <DialogContent className="bg-[#141414] border border-white/10 rounded-none text-white max-w-lg max-h-[85vh] overflow-y-auto">
                  <DialogHeader><DialogTitle className="text-sm font-bold uppercase tracking-wider">Create Invoice</DialogTitle></DialogHeader>
                  {payResult ? (
                    <div className="space-y-4 mt-4">
                      <div className="border border-green-600/30 bg-green-600/5 p-4 text-center">
                        <Check size={24} className="text-green-500 mx-auto mb-2" />
                        <p className="text-sm font-bold text-green-400 mb-1">Invoice Created!</p>
                        <p className="text-2xl font-black text-white">Rs {payResult.amount?.toLocaleString('en-IN')}</p>
                        <p className="text-xs text-zinc-400 mt-1">Client will receive email & SMS notification</p>
                      </div>
                      {payResult.short_url && (
                        <div className="flex items-center gap-2 border border-white/10 p-3">
                          <input readOnly value={payResult.short_url} className="flex-1 bg-transparent text-xs text-zinc-300 outline-none" />
                          <button onClick={() => copyUrl(payResult.short_url)} className="text-zinc-400 hover:text-white"><Copy size={14} /></button>
                          <a href={payResult.short_url} target="_blank" rel="noreferrer" className="text-zinc-400 hover:text-white"><ExternalLink size={14} /></a>
                        </div>
                      )}
                      <button onClick={() => { setPayResult(null); setInvForm({ client_name: "", client_email: "", client_phone: "", project_name: "", notes: "", items: [{ name: "", amount: "", quantity: 1 }] }); }} className="w-full text-xs text-zinc-500 hover:text-white text-center py-2">Create Another</button>
                    </div>
                  ) : (
                    <form onSubmit={handleCreateInvoice} className="space-y-3 mt-4">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Client Name *</Label>
                          <Input value={invForm.client_name} onChange={e => setInvForm({...invForm, client_name: e.target.value})} required placeholder="Rahul Sharma" className="bg-transparent border-white/20 rounded-none text-white text-sm focus-visible:ring-0" />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Phone *</Label>
                          <Input value={invForm.client_phone} onChange={e => setInvForm({...invForm, client_phone: e.target.value})} required type="tel" inputMode="tel" autoComplete="tel" placeholder="+91 98765 43210" className="bg-transparent border-white/20 rounded-none text-white text-sm focus-visible:ring-0" />
                        </div>
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Email *</Label>
                        <Input value={invForm.client_email} onChange={e => setInvForm({...invForm, client_email: e.target.value})} required type="email" placeholder="rahul@email.com" className="bg-transparent border-white/20 rounded-none text-white text-sm focus-visible:ring-0" />
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Project Name *</Label>
                        <Input value={invForm.project_name} onChange={e => setInvForm({...invForm, project_name: e.target.value})} required placeholder="Singh Dental Website" className="bg-transparent border-white/20 rounded-none text-white text-sm focus-visible:ring-0" />
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2 block">Line Items</Label>
                        <div className="space-y-2">
                          {invForm.items.map((item, idx) => (
                            <div key={idx} className="flex gap-2 items-center">
                              <Input value={item.name} onChange={e => updateInvItem(idx, 'name', e.target.value)} placeholder="Item name" className="flex-1 bg-transparent border-white/20 rounded-none text-white text-xs focus-visible:ring-0" />
                              <Input value={item.amount} onChange={e => updateInvItem(idx, 'amount', e.target.value)} type="number" placeholder="Rs" className="w-24 bg-transparent border-white/20 rounded-none text-white text-xs focus-visible:ring-0" />
                              <Input value={item.quantity} onChange={e => updateInvItem(idx, 'quantity', e.target.value)} type="number" min="1" className="w-14 bg-transparent border-white/20 rounded-none text-white text-xs focus-visible:ring-0" />
                              {invForm.items.length > 1 && <button type="button" onClick={() => removeInvItem(idx)} className="text-zinc-500 hover:text-red-500"><X size={14} /></button>}
                            </div>
                          ))}
                          <button type="button" onClick={addInvItem} className="text-[10px] uppercase tracking-wider text-red-500 hover:text-red-400 font-bold flex items-center gap-1"><Plus size={12} /> Add Item</button>
                        </div>
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Notes</Label>
                        <Textarea value={invForm.notes} onChange={e => setInvForm({...invForm, notes: e.target.value})} rows={2} placeholder="Payment terms, additional info..." className="bg-transparent border-white/20 rounded-none text-white text-sm resize-none focus-visible:ring-0" />
                      </div>
                      <button type="submit" disabled={payLoading} data-testid="invoice-submit" className="w-full bg-red-600 text-white py-3 text-xs font-bold uppercase tracking-widest hover:bg-white hover:text-black transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                        {payLoading ? <><Loader2 size={14} className="animate-spin" /> Creating...</> : <><FileText size={14} /> Create Invoice</>}
                      </button>
                    </form>
                  )}
                </DialogContent>
              </Dialog>

              <Dialog open={subOpen} onOpenChange={(v) => { setSubOpen(v); if (!v) setPayResult(null); }}>
                <DialogTrigger asChild>
                  <button data-testid="create-subscription-btn" className="bg-[#141414] border border-white/10 text-white text-xs font-bold uppercase tracking-widest py-2 px-6 flex items-center gap-2 hover:border-green-600/50 hover:text-green-500 transition-colors">
                    <RefreshCw size={14} /> Autopay
                  </button>
                </DialogTrigger>
                <DialogContent className="bg-[#141414] border border-white/10 rounded-none text-white max-w-lg max-h-[85vh] overflow-y-auto">
                  <DialogHeader><DialogTitle className="text-sm font-bold uppercase tracking-wider">Setup Autopay / Subscription</DialogTitle></DialogHeader>
                  {payResult ? (
                    <div className="space-y-4 mt-4">
                      <div className="border border-green-600/30 bg-green-600/5 p-4 text-center">
                        <Check size={24} className="text-green-500 mx-auto mb-2" />
                        <p className="text-sm font-bold text-green-400 mb-1">Autopay Created!</p>
                        <p className="text-2xl font-black text-white">Rs {payResult.amount?.toLocaleString('en-IN')}/{payResult.interval}</p>
                        <p className="text-xs text-zinc-400 mt-1">Client will receive authorization link via SMS & email</p>
                      </div>
                      {payResult.short_url && (
                        <div className="flex items-center gap-2 border border-white/10 p-3">
                          <input readOnly value={payResult.short_url} className="flex-1 bg-transparent text-xs text-zinc-300 outline-none" />
                          <button onClick={() => copyUrl(payResult.short_url)} className="text-zinc-400 hover:text-white"><Copy size={14} /></button>
                          <a href={payResult.short_url} target="_blank" rel="noreferrer" className="text-zinc-400 hover:text-white"><ExternalLink size={14} /></a>
                        </div>
                      )}
                      <button onClick={() => shareWhatsApp({ ...subForm, amount: parseInt(subForm.amount), short_url: payResult.short_url })} className="w-full py-3 bg-[#25D366] text-white font-bold uppercase tracking-widest text-xs flex items-center justify-center gap-2 hover:brightness-110">
                        <Send size={14} /> Share on WhatsApp
                      </button>
                      <button onClick={() => { setPayResult(null); setSubForm({ client_name: "", client_email: "", client_phone: "", project_name: "", amount: "", interval: "monthly", total_count: "12", description: "" }); }} className="w-full text-xs text-zinc-500 hover:text-white text-center py-2">Create Another</button>
                    </div>
                  ) : (
                    <form onSubmit={handleCreateSubscription} className="space-y-3 mt-4">
                      <p className="text-[10px] text-zinc-500 leading-relaxed">Set up recurring autopay for maintenance, hosting, or EMI payments. Client authorizes once, gets charged automatically.</p>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Client Name *</Label>
                          <Input value={subForm.client_name} onChange={e => setSubForm({...subForm, client_name: e.target.value})} required placeholder="Rahul Sharma" className="bg-transparent border-white/20 rounded-none text-white text-sm focus-visible:ring-0" />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Phone *</Label>
                          <Input value={subForm.client_phone} onChange={e => setSubForm({...subForm, client_phone: e.target.value})} required type="tel" inputMode="tel" autoComplete="tel" placeholder="+91 98765 43210" className="bg-transparent border-white/20 rounded-none text-white text-sm focus-visible:ring-0" />
                        </div>
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Email *</Label>
                        <Input value={subForm.client_email} onChange={e => setSubForm({...subForm, client_email: e.target.value})} required type="email" placeholder="rahul@email.com" className="bg-transparent border-white/20 rounded-none text-white text-sm focus-visible:ring-0" />
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Project / Service *</Label>
                        <Input value={subForm.project_name} onChange={e => setSubForm({...subForm, project_name: e.target.value})} required placeholder="e.g. Website Maintenance, Hosting" className="bg-transparent border-white/20 rounded-none text-white text-sm focus-visible:ring-0" />
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Amount (Rs) *</Label>
                          <Input value={subForm.amount} onChange={e => setSubForm({...subForm, amount: e.target.value})} required type="number" min="1" placeholder="500" className="bg-transparent border-white/20 rounded-none text-white text-sm focus-visible:ring-0" />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Frequency *</Label>
                          <select value={subForm.interval} onChange={e => setSubForm({...subForm, interval: e.target.value})} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2 text-sm text-white outline-none">
                            <option value="weekly">Weekly</option>
                            <option value="monthly">Monthly</option>
                            <option value="yearly">Yearly</option>
                          </select>
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Cycles *</Label>
                          <Input value={subForm.total_count} onChange={e => setSubForm({...subForm, total_count: e.target.value})} required type="number" min="1" placeholder="12" className="bg-transparent border-white/20 rounded-none text-white text-sm focus-visible:ring-0" />
                        </div>
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Description</Label>
                        <Input value={subForm.description} onChange={e => setSubForm({...subForm, description: e.target.value})} placeholder="Monthly website maintenance" className="bg-transparent border-white/20 rounded-none text-white text-sm focus-visible:ring-0" />
                      </div>
                      <button type="submit" disabled={payLoading} data-testid="subscription-submit" className="w-full bg-green-600 text-white py-3 text-xs font-bold uppercase tracking-widest hover:bg-white hover:text-black transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                        {payLoading ? <><Loader2 size={14} className="animate-spin" /> Creating...</> : <><RefreshCw size={14} /> Create Autopay</>}
                      </button>
                    </form>
                  )}
                </DialogContent>
              </Dialog>

              <Dialog open={advOpen} onOpenChange={(v) => { setAdvOpen(v); if (!v) setAdvResult(null); }}>
                <DialogTrigger asChild>
                  <button data-testid="create-advance-invoice-btn" className="bg-[#141414] border border-white/10 text-white text-xs font-bold uppercase tracking-widest py-2 px-6 flex items-center gap-2 hover:border-amber-500/60 hover:text-amber-400 transition-colors">
                    <FileText size={14} /> Advance Invoice
                  </button>
                </DialogTrigger>
                <DialogContent className="bg-[#141414] border border-white/10 rounded-none text-white max-w-lg max-h-[85vh] overflow-y-auto" data-testid="advance-invoice-modal">
                  <DialogHeader><DialogTitle className="text-sm font-bold uppercase tracking-wider">Advance / Proforma Invoice</DialogTitle></DialogHeader>
                  {advResult ? (
                    <div className="space-y-4 mt-4">
                      <div className="border border-amber-600/30 bg-amber-600/5 p-4 text-center">
                        <Check size={24} className="text-amber-400 mx-auto mb-2" />
                        <p className="text-sm font-bold text-amber-400 mb-1">Proforma Issued</p>
                        <p className="text-xl font-black text-white font-mono">{advResult.number}</p>
                        <p className="text-2xl font-black text-white mt-2">Rs {Number(advResult.amount).toLocaleString('en-IN')}</p>
                        <p className="text-[10px] text-zinc-500 mt-2 uppercase tracking-widest">
                          {advForm.send_email ? "Email sent to client with PDF attached" : "PDF generated — not emailed"}
                        </p>
                      </div>
                      <button
                        onClick={() => downloadAdvancePdf(advResult.number)}
                        data-testid="advance-download-pdf"
                        className="w-full py-3 bg-red-600 text-white font-bold uppercase tracking-widest text-xs flex items-center justify-center gap-2 hover:bg-white hover:text-black transition-colors border-2 border-white"
                      >
                        <FileText size={14} /> Download PDF
                      </button>
                      <button onClick={() => { setAdvResult(null); setAdvForm({ client_name: "", client_email: "", client_phone: "", project_name: "", description: "", amount: "", project_total: "", due_date: "", payment_terms: "", send_email: true }); }} className="w-full text-xs text-zinc-500 hover:text-white text-center py-2">Create Another</button>
                    </div>
                  ) : (
                    <form onSubmit={handleCreateAdvance} className="space-y-3 mt-4">
                      <p className="text-[10px] text-zinc-500 leading-relaxed">
                        Issue a formal PDF advance / proforma invoice BEFORE the client pays. Great for asking upfront deposits, retainers, or 50%-start payments. A final paid invoice auto-issues later when the real payment lands.
                      </p>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Client Name *</Label>
                          <Input data-testid="advance-client-name" value={advForm.client_name} onChange={e => setAdvForm({...advForm, client_name: e.target.value})} required placeholder="Bangla Darshan" className="bg-transparent border-white/20 rounded-none text-white text-sm focus-visible:ring-0" />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Phone</Label>
                          <Input value={advForm.client_phone} onChange={e => setAdvForm({...advForm, client_phone: e.target.value})} type="tel" inputMode="tel" placeholder="+91 98765 43210" className="bg-transparent border-white/20 rounded-none text-white text-sm focus-visible:ring-0" />
                        </div>
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Client Email *</Label>
                        <Input data-testid="advance-client-email" value={advForm.client_email} onChange={e => setAdvForm({...advForm, client_email: e.target.value})} required type="email" inputMode="email" placeholder="client@email.com" className="bg-transparent border-white/20 rounded-none text-white text-sm focus-visible:ring-0" />
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Project / Service *</Label>
                        <Input data-testid="advance-project" value={advForm.project_name} onChange={e => setAdvForm({...advForm, project_name: e.target.value})} required placeholder="Website design — 50% advance" className="bg-transparent border-white/20 rounded-none text-white text-sm focus-visible:ring-0" />
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Line-item description</Label>
                        <textarea value={advForm.description} onChange={e => setAdvForm({...advForm, description: e.target.value})} rows={2} placeholder="What's included: 5 pages, SEO setup, contact form…" className="w-full bg-transparent border border-white/20 px-3 py-2 text-sm text-white outline-none focus:border-red-600 resize-none" />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Amount to collect (Rs) *</Label>
                          <Input data-testid="advance-amount" value={advForm.amount} onChange={e => setAdvForm({...advForm, amount: e.target.value})} required type="number" min="1" inputMode="numeric" placeholder="13750" className="bg-transparent border-white/20 rounded-none text-white text-sm focus-visible:ring-0" />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Project total (optional)</Label>
                          <Input value={advForm.project_total} onChange={e => setAdvForm({...advForm, project_total: e.target.value})} type="number" min="0" inputMode="numeric" placeholder="27500" title="If this is an advance on a bigger project, type full cost so the paid invoice shows PARTIALLY PAID." className="bg-transparent border-white/20 rounded-none text-white text-sm focus-visible:ring-0" />
                        </div>
                      </div>
                      {advForm.amount && advForm.project_total && Number(advForm.project_total) > Number(advForm.amount) && (
                        <p className="text-[10px] text-amber-400" data-testid="advance-preview-hint">
                          On payment, the generated invoice will show <strong>PARTIALLY PAID</strong> — Rs {Number(advForm.amount).toLocaleString('en-IN')} of Rs {Number(advForm.project_total).toLocaleString('en-IN')} · balance Rs {Number(Number(advForm.project_total) - Number(advForm.amount)).toLocaleString('en-IN')}.
                        </p>
                      )}
                      <div>
                        <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Due date</Label>
                        <Input value={advForm.due_date} onChange={e => setAdvForm({...advForm, due_date: e.target.value})} type="date" className="bg-transparent border-white/20 rounded-none text-white text-sm focus-visible:ring-0" />
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Payment terms (optional)</Label>
                        <textarea value={advForm.payment_terms} onChange={e => setAdvForm({...advForm, payment_terms: e.target.value})} rows={2} placeholder="Pay via Razorpay link / UPI kamran@ybl. Project starts on receipt." className="w-full bg-transparent border border-white/20 px-3 py-2 text-sm text-white outline-none focus:border-red-600 resize-none" />
                      </div>
                      <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer">
                        <input type="checkbox" checked={!!advForm.send_email} onChange={e => setAdvForm({...advForm, send_email: e.target.checked})} data-testid="advance-send-email" className="w-4 h-4 accent-red-600" />
                        <span className="uppercase tracking-wider">Email PDF to client immediately</span>
                      </label>
                      <button type="submit" disabled={payLoading} data-testid="advance-submit" className="w-full bg-amber-500 text-black py-3 text-xs font-bold uppercase tracking-widest hover:bg-white transition-colors disabled:opacity-50 flex items-center justify-center gap-2 border-2 border-white">
                        {payLoading ? <><Loader2 size={14} className="animate-spin" /> Issuing…</> : <><FileText size={14} /> Issue Proforma</>}
                      </button>
                    </form>
                  )}
                </DialogContent>
              </Dialog>

              <Dialog open={balOpen} onOpenChange={(v) => { setBalOpen(v); if (!v) setBalResult(null); }}>
                <DialogTrigger asChild>
                  <button data-testid="create-balance-invoice-btn" className="bg-[#141414] border border-white/10 text-white text-xs font-bold uppercase tracking-widest py-2 px-6 flex items-center gap-2 hover:border-emerald-500/60 hover:text-emerald-400 transition-colors">
                    <FileText size={14} /> Balance Invoice
                  </button>
                </DialogTrigger>
                <DialogContent className="bg-[#141414] border border-white/10 rounded-none text-white max-w-lg max-h-[85vh] overflow-y-auto" data-testid="balance-invoice-modal">
                  <DialogHeader><DialogTitle className="text-sm font-bold uppercase tracking-wider">Balance / Settlement Invoice</DialogTitle></DialogHeader>
                  {balResult ? (
                    <div className="space-y-4 mt-4">
                      <div className="border border-emerald-600/30 bg-emerald-600/5 p-4 text-center">
                        <Check size={24} className="text-emerald-400 mx-auto mb-2" />
                        <p className="text-sm font-bold text-emerald-400 mb-1">Balance Invoice Issued</p>
                        <p className="text-xl font-black text-white font-mono">{balResult.number}</p>
                        <p className="text-[10px] text-zinc-500 mt-2 uppercase tracking-widest">Balance Due</p>
                        <p className="text-2xl font-black text-white">Rs {Number(balResult.amount).toLocaleString('en-IN')}</p>
                        <p className="text-[10px] text-zinc-500 mt-1">
                          Project total Rs {Number(balResult.project_total).toLocaleString('en-IN')} · Advance Rs {Number(balResult.advance_paid).toLocaleString('en-IN')}
                        </p>
                        <p className="text-[10px] text-zinc-500 mt-2 uppercase tracking-widest">
                          {balForm.send_email ? "Email sent to client with PDF attached" : "PDF generated — not emailed"}
                        </p>
                      </div>
                      <button
                        onClick={() => downloadAdvancePdf(balResult.number)}
                        data-testid="balance-download-pdf"
                        className="w-full py-3 bg-red-600 text-white font-bold uppercase tracking-widest text-xs flex items-center justify-center gap-2 hover:bg-white hover:text-black transition-colors border-2 border-white"
                      >
                        <FileText size={14} /> Download PDF
                      </button>
                      <button onClick={() => { setBalResult(null); setBalForm({ client_name: "", client_email: "", client_phone: "", project_name: "", description: "", project_total: "", advance_paid: "", advance_ref: "", due_date: "", payment_terms: "", send_email: true }); }} className="w-full text-xs text-zinc-500 hover:text-white text-center py-2">Create Another</button>
                    </div>
                  ) : (
                    <form onSubmit={handleCreateBalance} className="space-y-3 mt-4">
                      <p className="text-[10px] text-zinc-500 leading-relaxed">
                        Use this AFTER the client has already paid an advance. The invoice renders a breakdown table (total − advance = balance) and the balance becomes the TOTAL to pay. On settlement, a final paid invoice auto-issues.
                      </p>
                      {/* Quick-pick: auto-fill from an existing proforma */}
                      {proformas.length > 0 && (
                        <div className="border border-emerald-600/30 bg-emerald-600/5 p-3">
                          <Label className="text-[10px] uppercase tracking-wider text-emerald-400 mb-1.5 block flex items-center gap-1.5">
                            <Sparkles size={10} /> Auto-fill from proforma
                          </Label>
                          <select
                            data-testid="balance-pick-proforma"
                            value=""
                            onChange={(e) => {
                              const num = e.target.value;
                              if (!num) return;
                              const p = proformas.find((x) => x.number === num);
                              if (!p) return;
                              setBalForm({
                                ...balForm,
                                client_name: p.client_name || balForm.client_name,
                                client_email: p.client_email || balForm.client_email,
                                client_phone: p.client_phone || balForm.client_phone,
                                project_name: p.project_name || balForm.project_name,
                                description: balForm.description || (p.description ? `Final balance — ${p.description}` : "Final balance on delivery"),
                                advance_paid: String(p.amount ?? balForm.advance_paid ?? ""),
                                advance_ref: p.number,
                                project_total: balForm.project_total || (p.project_total ? String(p.project_total) : ""),
                              });
                              toast.success(`Auto-filled from ${p.number}`);
                            }}
                            className="w-full bg-[#0A0A0A] border border-white/15 px-3 py-2 text-xs text-white outline-none focus:border-emerald-500 cursor-pointer font-mono"
                          >
                            <option value="">— Select a proforma to copy details from —</option>
                            {proformas.map((p) => {
                              const advanceTxt = `Rs ${Number(p.amount || 0).toLocaleString('en-IN')}`;
                              const totalTxt = p.project_total ? ` of Rs ${Number(p.project_total).toLocaleString('en-IN')}` : "";
                              const statusTxt = p.status === "paid" ? "✓ paid" : p.status === "paid_partial" ? "◐ partial" : "due";
                              return (
                                <option key={p.number} value={p.number}>
                                  {p.number} · {p.client_name || "—"} · {advanceTxt}{totalTxt} · {statusTxt}
                                </option>
                              );
                            })}
                          </select>
                          <p className="text-[9px] text-zinc-600 mt-1.5 leading-relaxed">Picks up client, project, advance amount, and reference. You can still edit any field below.</p>
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Client Name *</Label>
                          <Input data-testid="balance-client-name" value={balForm.client_name} onChange={e => setBalForm({...balForm, client_name: e.target.value})} required placeholder="Bangla Darshan" className="bg-transparent border-white/20 rounded-none text-white text-sm focus-visible:ring-0" />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Phone</Label>
                          <Input value={balForm.client_phone} onChange={e => setBalForm({...balForm, client_phone: e.target.value})} type="tel" inputMode="tel" placeholder="+91 98765 43210" className="bg-transparent border-white/20 rounded-none text-white text-sm focus-visible:ring-0" />
                        </div>
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Client Email *</Label>
                        <Input data-testid="balance-client-email" value={balForm.client_email} onChange={e => setBalForm({...balForm, client_email: e.target.value})} required type="email" inputMode="email" placeholder="client@email.com" className="bg-transparent border-white/20 rounded-none text-white text-sm focus-visible:ring-0" />
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Project Name *</Label>
                        <Input data-testid="balance-project" value={balForm.project_name} onChange={e => setBalForm({...balForm, project_name: e.target.value})} required placeholder="Tourism website · settlement" className="bg-transparent border-white/20 rounded-none text-white text-sm focus-visible:ring-0" />
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Line-item description</Label>
                        <textarea value={balForm.description} onChange={e => setBalForm({...balForm, description: e.target.value})} rows={2} placeholder="Final balance on delivery — all pages live, SEO indexed, handover complete" className="w-full bg-transparent border border-white/20 px-3 py-2 text-sm text-white outline-none focus:border-red-600 resize-none" />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Project total (Rs) *</Label>
                          <Input data-testid="balance-total" value={balForm.project_total} onChange={e => setBalForm({...balForm, project_total: e.target.value})} required type="number" min="1" inputMode="numeric" placeholder="27500" className="bg-transparent border-white/20 rounded-none text-white text-sm focus-visible:ring-0" />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Advance paid (Rs) *</Label>
                          <Input data-testid="balance-advance" value={balForm.advance_paid} onChange={e => setBalForm({...balForm, advance_paid: e.target.value})} required type="number" min="0" inputMode="numeric" placeholder="13750" className="bg-transparent border-white/20 rounded-none text-white text-sm focus-visible:ring-0" />
                        </div>
                      </div>
                      {balForm.project_total && balForm.advance_paid && !isNaN(Number(balForm.project_total)) && !isNaN(Number(balForm.advance_paid)) && (
                        <div className={`border p-3 flex items-center justify-between ${Number(balForm.project_total) - Number(balForm.advance_paid) > 0 ? "border-emerald-600/30 bg-emerald-600/5" : "border-red-600/40 bg-red-600/10"}`} data-testid="balance-preview">
                          <span className="text-[10px] uppercase tracking-widest text-zinc-400">Balance due</span>
                          <span className={`text-lg font-black ${Number(balForm.project_total) - Number(balForm.advance_paid) > 0 ? "text-emerald-400" : "text-red-500"}`}>
                            Rs {Number(Number(balForm.project_total) - Number(balForm.advance_paid)).toLocaleString('en-IN')}
                          </span>
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Advance invoice ref</Label>
                          <Input value={balForm.advance_ref} onChange={e => setBalForm({...balForm, advance_ref: e.target.value})} placeholder="KOKO-PROF-2026-0001" className="bg-transparent border-white/20 rounded-none text-white text-sm focus-visible:ring-0 font-mono" />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Due date</Label>
                          <Input value={balForm.due_date} onChange={e => setBalForm({...balForm, due_date: e.target.value})} type="date" className="bg-transparent border-white/20 rounded-none text-white text-sm focus-visible:ring-0" />
                        </div>
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Payment terms (optional)</Label>
                        <textarea value={balForm.payment_terms} onChange={e => setBalForm({...balForm, payment_terms: e.target.value})} rows={2} placeholder="Pay via Razorpay link or UPI kamran@ybl. Final paid invoice issues automatically on receipt." className="w-full bg-transparent border border-white/20 px-3 py-2 text-sm text-white outline-none focus:border-red-600 resize-none" />
                      </div>
                      <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer">
                        <input type="checkbox" checked={!!balForm.send_email} onChange={e => setBalForm({...balForm, send_email: e.target.checked})} data-testid="balance-send-email" className="w-4 h-4 accent-red-600" />
                        <span className="uppercase tracking-wider">Email PDF to client immediately</span>
                      </label>
                      <button type="submit" disabled={payLoading} data-testid="balance-submit" className="w-full bg-emerald-500 text-black py-3 text-xs font-bold uppercase tracking-widest hover:bg-white transition-colors disabled:opacity-50 flex items-center justify-center gap-2 border-2 border-white">
                        {payLoading ? <><Loader2 size={14} className="animate-spin" /> Issuing…</> : <><FileText size={14} /> Issue Balance Invoice</>}
                      </button>
                    </form>
                  )}
                </DialogContent>
              </Dialog>

              <Dialog open={recOpen} onOpenChange={(v) => { setRecOpen(v); if (!v) setRecResult(null); }}>
                <DialogTrigger asChild>
                  <button data-testid="record-payment-btn" onClick={() => { setRecResult(null); setRecForm({ client_name: "", client_email: "", client_phone: "", project_name: "", description: "", amount: "", project_total: "", source_invoice_number: "", payment_method: localStorage.getItem("koko_last_payment_method") || "cash", payment_ref: "", payment_date: "", send_email: true }); }} className="bg-[#141414] border border-white/10 text-white text-xs font-bold uppercase tracking-widest py-2 px-6 flex items-center gap-2 hover:border-sky-500/60 hover:text-sky-400 transition-colors">
                    <IndianRupee size={14} /> Record Payment
                  </button>
                </DialogTrigger>
                <DialogContent className="bg-[#141414] border border-white/10 rounded-none text-white max-w-lg max-h-[85vh] overflow-y-auto" data-testid="record-payment-modal">
                  <DialogHeader><DialogTitle className="text-sm font-bold uppercase tracking-wider">Record Payment (Cash / UPI / Bank / Cheque)</DialogTitle></DialogHeader>
                  {recResult ? (
                    <div className="space-y-4 mt-4">
                      <div className={`border p-4 text-center ${recResult.partial_payment ? "border-amber-600/30 bg-amber-600/5" : "border-emerald-600/30 bg-emerald-600/5"}`}>
                        <Check size={24} className={`${recResult.partial_payment ? "text-amber-400" : "text-emerald-400"} mx-auto mb-2`} />
                        <p className={`text-sm font-bold ${recResult.partial_payment ? "text-amber-400" : "text-emerald-400"} mb-1`}>
                          {recResult.partial_payment ? "PART-PAYMENT RECEIPT Generated" : "PAID Invoice Generated"}
                        </p>
                        <p className="text-xl font-black text-white font-mono">{recResult.number}</p>
                        <p className="text-2xl font-black text-white mt-2">Rs {Number(recResult.amount).toLocaleString('en-IN')}</p>
                        {recResult.partial_payment && recResult.project_total && (
                          <p className="text-[10px] text-amber-300 mt-2">Balance remaining: Rs {Number(recResult.project_total - recResult.amount - (recResult.prior_paid || 0)).toLocaleString('en-IN')}</p>
                        )}
                        <p className="text-[10px] text-zinc-500 mt-2 uppercase tracking-widest">
                          {recForm.send_email ? "Email sent to client with PDF attached" : "PDF generated — not emailed"}
                        </p>
                      </div>
                      <button onClick={() => downloadAdvancePdf(recResult.number)} data-testid="record-download-pdf" className="w-full py-3 bg-red-600 text-white font-bold uppercase tracking-widest text-xs flex items-center justify-center gap-2 hover:bg-white hover:text-black transition-colors border-2 border-white">
                        <FileText size={14} /> Download PDF
                      </button>
                      <button onClick={() => { setRecResult(null); setRecForm({ client_name: "", client_email: "", client_phone: "", project_name: "", description: "", amount: "", project_total: "", source_invoice_number: "", payment_method: localStorage.getItem("koko_last_payment_method") || "cash", payment_ref: "", payment_date: "", send_email: true }); }} className="w-full text-xs text-zinc-500 hover:text-white text-center py-2">Record Another</button>
                    </div>
                  ) : (
                    <form onSubmit={handleRecordPayment} className="space-y-3 mt-4">
                      <p className="text-[10px] text-zinc-500 leading-relaxed">
                        Use this for cash, UPI, bank transfer, or cheque payments received offline. Creates a PAID invoice (or PARTIALLY PAID if the payment is less than project total) exactly like Razorpay does.
                      </p>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Client Name *</Label>
                          <Input data-testid="record-client-name" value={recForm.client_name} onChange={e => setRecForm({...recForm, client_name: e.target.value})} required placeholder="Bangla Darshan" className="bg-transparent border-white/20 rounded-none text-white text-sm focus-visible:ring-0" />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Phone</Label>
                          <Input value={recForm.client_phone} onChange={e => setRecForm({...recForm, client_phone: e.target.value})} type="tel" inputMode="tel" placeholder="+91 98765 43210" className="bg-transparent border-white/20 rounded-none text-white text-sm focus-visible:ring-0" />
                        </div>
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Client Email *</Label>
                        <Input data-testid="record-client-email" value={recForm.client_email} onChange={e => setRecForm({...recForm, client_email: e.target.value})} required type="email" inputMode="email" placeholder="client@email.com" className="bg-transparent border-white/20 rounded-none text-white text-sm focus-visible:ring-0" />
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Project / Service *</Label>
                        <Input data-testid="record-project" value={recForm.project_name} onChange={e => setRecForm({...recForm, project_name: e.target.value})} required placeholder="Tourism website" className="bg-transparent border-white/20 rounded-none text-white text-sm focus-visible:ring-0" />
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Description</Label>
                        <textarea value={recForm.description} onChange={e => setRecForm({...recForm, description: e.target.value})} rows={2} placeholder="Cash received at meeting on 21 Apr…" className="w-full bg-transparent border border-white/20 px-3 py-2 text-sm text-white outline-none focus:border-red-600 resize-none" />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Amount received (Rs) *</Label>
                          <Input data-testid="record-amount" value={recForm.amount} onChange={e => setRecForm({...recForm, amount: e.target.value})} required type="number" min="1" inputMode="numeric" placeholder="13750" className="bg-transparent border-white/20 rounded-none text-white text-sm focus-visible:ring-0" />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Project total (optional)</Label>
                          <Input value={recForm.project_total} onChange={e => setRecForm({...recForm, project_total: e.target.value})} type="number" min="0" inputMode="numeric" placeholder="27500" title="Set if this is a partial on a bigger project → invoice will say PARTIALLY PAID." className="bg-transparent border-white/20 rounded-none text-white text-sm focus-visible:ring-0" />
                        </div>
                      </div>
                      {recForm.amount && recForm.project_total && Number(recForm.project_total) > Number(recForm.amount) && (
                        <p className="text-[10px] text-amber-400" data-testid="record-preview-hint">
                          Invoice will say <strong>PARTIALLY PAID</strong> — balance Rs {Number(Number(recForm.project_total) - Number(recForm.amount)).toLocaleString('en-IN')} remaining.
                        </p>
                      )}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Method *</Label>
                          <select data-testid="record-method" value={recForm.payment_method} onChange={e => setRecForm({...recForm, payment_method: e.target.value})} className="w-full bg-[#141414] border border-white/20 px-3 py-2 text-sm text-white outline-none focus:border-red-600 uppercase tracking-wider">
                            <option value="cash">Cash</option>
                            <option value="upi">UPI</option>
                            <option value="bank_transfer">Bank transfer</option>
                            <option value="cheque">Cheque</option>
                            <option value="other">Other</option>
                          </select>
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Reference</Label>
                          <Input value={recForm.payment_ref} onChange={e => setRecForm({...recForm, payment_ref: e.target.value})} placeholder="UPI txn ID / Cheque no / …" className="bg-transparent border-white/20 rounded-none text-white text-sm focus-visible:ring-0" />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Source invoice (optional)</Label>
                          <Input value={recForm.source_invoice_number} onChange={e => setRecForm({...recForm, source_invoice_number: e.target.value})} placeholder="KOKO-PROF-2026-0001" className="bg-transparent border-white/20 rounded-none text-white text-sm focus-visible:ring-0 font-mono" />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Payment date</Label>
                          <Input value={recForm.payment_date} onChange={e => setRecForm({...recForm, payment_date: e.target.value})} type="date" className="bg-transparent border-white/20 rounded-none text-white text-sm focus-visible:ring-0" />
                        </div>
                      </div>
                      <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer">
                        <input type="checkbox" checked={!!recForm.send_email} onChange={e => setRecForm({...recForm, send_email: e.target.checked})} data-testid="record-send-email" className="w-4 h-4 accent-red-600" />
                        <span className="uppercase tracking-wider">Email receipt PDF to client</span>
                      </label>
                      <button type="submit" disabled={payLoading} data-testid="record-submit" className="w-full bg-sky-500 text-black py-3 text-xs font-bold uppercase tracking-widest hover:bg-white transition-colors disabled:opacity-50 flex items-center justify-center gap-2 border-2 border-white">
                        {payLoading ? <><Loader2 size={14} className="animate-spin" /> Recording…</> : <><Check size={14} /> Record Payment &amp; Issue Invoice</>}
                      </button>
                    </form>
                  )}
                </DialogContent>
              </Dialog>
            </div>

            {proformas.length > 0 && (
              <div className="mt-4" data-testid="admin-proformas-list">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">
                  Proforma / Advance Invoices ({proformas.length})
                </p>
                <div className="grid gap-2">
                  {proformas.slice(0, 10).map((p) => (
                    <div key={p.number} data-testid={`proforma-${p.number}`} className="border border-amber-600/20 bg-amber-600/5 p-3 flex items-center justify-between gap-3 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-black text-white font-mono">{p.number}</span>
                          <span className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 bg-amber-600/20 text-amber-300 border border-amber-600/40">Due</span>
                          <span className="text-[10px] text-zinc-500 truncate">· {p.client_name} · {p.project_name}</span>
                        </div>
                        <p className="text-[10px] text-zinc-500 mt-0.5">
                          Issued {p.issued_at_str}{p.due_date_str ? ` · Due by ${p.due_date_str}` : ""}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-sm font-black text-amber-300">Rs {Number(p.amount).toLocaleString('en-IN')}</span>
                        {p.status !== "paid" && p.status !== "paid_partial" && (
                          <>
                            <button onClick={() => collectOnInvoice(p)} title="Send Razorpay payment link" data-testid={`collect-proforma-${p.number}`} className="bg-red-600 text-white text-[10px] font-bold uppercase tracking-widest px-3 py-2 hover:bg-white hover:text-black border border-white transition-colors">
                              Collect
                            </button>
                            <button onClick={() => openRecordForInvoice(p)} title="Record cash / UPI / bank payment" data-testid={`record-cash-proforma-${p.number}`} className="bg-sky-500/10 border border-sky-500/40 text-sky-300 text-[10px] font-bold uppercase tracking-widest px-3 py-2 hover:bg-sky-500 hover:text-black transition-colors">
                              Cash
                            </button>
                          </>
                        )}
                        {(p.status === "paid" || p.status === "paid_partial") && (
                          <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 border ${p.status === "paid" ? "bg-emerald-600/20 text-emerald-300 border-emerald-600/40" : "bg-amber-600/20 text-amber-300 border-amber-600/40"}`}>
                            {p.status === "paid" ? "Paid" : "Partially Paid"}
                          </span>
                        )}
                        <button onClick={() => downloadAdvancePdf(p.number)} title="Download PDF" className="bg-amber-600/10 border border-amber-600/30 text-amber-300 p-2 hover:bg-amber-500 hover:text-black transition-colors">
                          <FileText size={12} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {balances.length > 0 && (
              <div className="mt-4" data-testid="admin-balances-list">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">
                  Balance / Settlement Invoices ({balances.length})
                </p>
                <div className="grid gap-2">
                  {balances.slice(0, 10).map((b) => (
                    <div key={b.number} data-testid={`balance-${b.number}`} className="border border-emerald-600/20 bg-emerald-600/5 p-3 flex items-center justify-between gap-3 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-black text-white font-mono">{b.number}</span>
                          <span className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 bg-emerald-600/20 text-emerald-300 border border-emerald-600/40">Balance Due</span>
                          <span className="text-[10px] text-zinc-500 truncate">· {b.client_name} · {b.project_name}</span>
                        </div>
                        <p className="text-[10px] text-zinc-500 mt-0.5">
                          Total Rs {Number(b.project_total).toLocaleString('en-IN')} · Advance Rs {Number(b.advance_paid).toLocaleString('en-IN')}
                          {b.due_date_str ? ` · Due ${b.due_date_str}` : ""}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-sm font-black text-emerald-300">Rs {Number(b.amount).toLocaleString('en-IN')}</span>
                        {b.status !== "paid" && b.status !== "paid_partial" && (
                          <>
                            <button onClick={() => collectOnInvoice(b)} title="Send Razorpay payment link" data-testid={`collect-balance-${b.number}`} className="bg-red-600 text-white text-[10px] font-bold uppercase tracking-widest px-3 py-2 hover:bg-white hover:text-black border border-white transition-colors">
                              Collect
                            </button>
                            <button onClick={() => openRecordForInvoice(b)} title="Record cash / UPI / bank payment" data-testid={`record-cash-balance-${b.number}`} className="bg-sky-500/10 border border-sky-500/40 text-sky-300 text-[10px] font-bold uppercase tracking-widest px-3 py-2 hover:bg-sky-500 hover:text-black transition-colors">
                              Cash
                            </button>
                          </>
                        )}
                        {b.status === "paid" && (
                          <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 border bg-emerald-600/20 text-emerald-300 border-emerald-600/40">Paid</span>
                        )}
                        <button onClick={() => downloadAdvancePdf(b.number)} title="Download PDF" className="bg-emerald-600/10 border border-emerald-600/30 text-emerald-300 p-2 hover:bg-emerald-500 hover:text-black transition-colors">
                          <FileText size={12} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Payment History */}
            {payments.length === 0 && paymentsLoaded && <p className="text-zinc-500 text-sm text-center py-12">No payments yet. Create a payment link or invoice above.</p>}
            {!paymentsLoaded && <SkeletonList count={3} />}
            {payments.map((p, i) => (
              <div key={i} data-testid={`payment-${i}`} className="border border-white/10 bg-[#141414] p-5 flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <span className="font-bold text-white text-sm uppercase tracking-wider">{p.client_name}</span>
                    <Badge className={`rounded-none text-[10px] uppercase tracking-wider ${p.type === 'invoice' ? 'bg-blue-600/10 text-blue-400 border-blue-600/20' : p.type === 'subscription' ? 'bg-green-600/10 text-green-400 border-green-600/20' : 'bg-purple-600/10 text-purple-400 border-purple-600/20'}`} variant="outline">{p.type === 'invoice' ? 'Invoice' : p.type === 'subscription' ? 'Autopay' : 'Pay Link'}</Badge>
                    <Badge className={`rounded-none text-[10px] uppercase tracking-wider ${p.status === 'paid' ? 'bg-green-600/10 text-green-400 border-green-600/20' : p.status === 'expired' ? 'bg-red-600/10 text-red-400 border-red-600/20' : 'bg-yellow-600/10 text-yellow-400 border-yellow-600/20'}`} variant="outline">{p.status}</Badge>
                  </div>
                  <p className="text-xs text-zinc-400 mb-1">{p.project_name} {p.description ? `— ${p.description}` : ''}</p>
                  <p className="text-xs text-zinc-600">{p.client_email} {p.client_phone ? `· ${p.client_phone}` : ''} · {new Date(p.created_at).toLocaleDateString('en-IN')}</p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <p className="text-lg font-black text-white">Rs {p.amount?.toLocaleString('en-IN')}</p>
                  <div className="flex gap-1">
                    {p.short_url && (
                      <>
                        <button onClick={() => copyUrl(p.short_url)} title="Copy link" className="bg-[#0A0A0A] border border-white/10 text-zinc-400 p-2 hover:text-white transition-colors"><Copy size={14} /></button>
                        <button onClick={() => shareWhatsApp(p)} title="Share on WhatsApp" className="bg-[#25D366]/10 border border-[#25D366]/20 text-[#25D366] p-2 hover:bg-[#25D366] hover:text-white transition-colors"><Send size={14} /></button>
                      </>
                    )}
                    {p.status === 'paid' && (
                      <button
                        onClick={() => downloadAdminInvoice(p.razorpay_id)}
                        title="Download invoice PDF"
                        data-testid={`payment-invoice-dl-${i}`}
                        className="bg-red-600/10 border border-red-600/30 text-red-400 p-2 hover:bg-red-600 hover:text-white transition-colors"
                      >
                        <FileText size={14} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="ai-email" className="space-y-6">
            {aiSent ? (
              <div className="max-w-2xl mx-auto text-center py-12">
                <div className="border border-green-600/30 bg-green-600/5 p-8 mb-6">
                  <Check size={32} className="text-green-500 mx-auto mb-3" />
                  <h3 className="text-lg font-black uppercase tracking-tight text-white mb-2">Email Sent!</h3>
                  <p className="text-sm text-zinc-400">Sent to <span className="text-white font-bold">{aiTo}</span></p>
                  <p className="text-xs text-zinc-500 mt-1">Subject: {aiDraft?.subject}</p>
                </div>
                <button onClick={resetAiEmail} data-testid="ai-email-reset" className="bg-red-600 text-white font-bold uppercase tracking-widest py-3 px-8 text-xs hover:bg-white hover:text-black transition-all">
                  Compose Another
                </button>
              </div>
            ) : aiDraft ? (
              <div className="max-w-2xl mx-auto">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-white flex items-center gap-2"><Pencil size={14} className="text-red-500" /> Review & Send</h3>
                  <button onClick={() => { setAiDraft(null); setAiSent(false); }} className="text-xs text-zinc-500 hover:text-white uppercase tracking-wider">Back to prompt</button>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Subject</Label>
                    <Input data-testid="ai-email-subject" value={aiDraft.subject} onChange={e => setAiDraft({...aiDraft, subject: e.target.value})} className="bg-transparent border-white/20 rounded-none text-white text-sm focus-visible:ring-0" />
                  </div>

                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Body <span className="text-zinc-700">(edit if needed)</span></Label>
                    <Textarea data-testid="ai-email-body" value={aiDraft.body} onChange={e => setAiDraft({...aiDraft, body: e.target.value})} rows={10} className="bg-[#141414] border-white/10 rounded-none text-white text-sm focus-visible:ring-0 resize-none leading-relaxed font-mono" />
                  </div>

                  <div className="border border-white/5 p-4">
                    <p className="text-[10px] uppercase tracking-wider text-zinc-600 mb-2">Preview</p>
                    <div className="bg-[#0A0A0A] border border-white/5 p-4">
                      <div className="bg-[#E50914] px-4 py-2 mb-4"><p className="text-sm font-bold text-white tracking-wider">KOKO SITES</p></div>
                      {aiDraft.body.split('\n').map((line, i) => (
                        <p key={i} className="text-xs text-zinc-400 leading-relaxed mb-1">{line || '\u00A0'}</p>
                      ))}
                      <div className="border-t border-white/5 mt-4 pt-3">
                        <p className="text-[10px] text-zinc-600">KoKo Sites — Bolpur Santiniketan, West Bengal | +91 92424 78118</p>
                      </div>
                    </div>
                  </div>

                  <div className="border border-red-600/20 bg-red-600/5 p-4">
                    <Label className="text-[10px] uppercase tracking-wider text-red-500 mb-1.5 block font-bold">Send To *</Label>
                    <Input data-testid="ai-email-to" value={aiTo} onChange={e => setAiTo(e.target.value)} type="email" placeholder="client@email.com" className="bg-transparent border-white/20 rounded-none text-white text-sm focus-visible:ring-0" />
                  </div>

                  {aiError && (
                    <div data-testid="ai-email-error" className={`border-2 p-4 flex items-start gap-3 ${aiError.code === "resend_domain_unverified" ? "border-yellow-500/60 bg-yellow-500/10" : "border-red-600/60 bg-red-600/10"}`}>
                      <AlertTriangle size={20} className={`shrink-0 mt-0.5 ${aiError.code === "resend_domain_unverified" ? "text-yellow-500" : "text-red-500"}`} />
                      <div className="min-w-0 flex-1">
                        <p className={`text-[11px] font-black uppercase tracking-widest ${aiError.code === "resend_domain_unverified" ? "text-yellow-400" : "text-red-400"}`}>
                          {aiError.code === "resend_domain_unverified" ? "Domain Not Verified in Resend" : aiError.code === "resend_quota" ? "Resend Quota Reached" : "Send Failed"}
                        </p>
                        <p className="text-sm text-white mt-1 leading-relaxed">{aiError.message}</p>
                        {aiError.code === "resend_domain_unverified" && (
                          <div className="mt-3 space-y-2 text-[12px] text-zinc-300 leading-relaxed">
                            <p className="text-yellow-400 font-bold">How to fix (5 min, one-time):</p>
                            <ol className="list-decimal pl-5 space-y-1">
                              <li>Open <a href="https://resend.com/domains" target="_blank" rel="noopener noreferrer" className="text-red-500 underline hover:text-red-400">resend.com/domains</a> (login as kokositesweb@gmail.com)</li>
                              <li>Click "Add Domain" → enter <code className="bg-black/40 px-1 text-white">kokosites.in</code></li>
                              <li>Add the 4 DNS records Resend shows into your domain DNS settings</li>
                              <li>Click "Verify DNS Records" in Resend (~5 min to propagate)</li>
                            </ol>
                            <p className="text-zinc-500 text-[11px] mt-2">Until then, you can only send to <code className="text-white bg-black/40 px-1">kokositesweb@gmail.com</code> (the Resend account owner).</p>
                          </div>
                        )}
                        {aiError.raw && aiError.code !== "resend_domain_unverified" && (
                          <p className="text-[11px] text-zinc-500 mt-2 font-mono break-all">raw: {aiError.raw}</p>
                        )}
                      </div>
                      <button onClick={() => setAiError(null)} aria-label="Dismiss" className="text-zinc-500 hover:text-white shrink-0">
                        <X size={16} />
                      </button>
                    </div>
                  )}

                  <div className="flex gap-3">
                    <button onClick={() => {
                      if (!aiTo.trim()) {
                        const el = document.querySelector('[data-testid="ai-email-to"]');
                        if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.focus(); }
                        return;
                      }
                      handleAiSend();
                    }} disabled={aiSending} data-testid="ai-email-send" className="flex-1 bg-red-600 text-white py-3 text-xs font-bold uppercase tracking-widest hover:bg-white hover:text-black transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                      {aiSending ? <><Loader2 size={14} className="animate-spin" /> Sending...</> : <><Send size={14} /> Send from kamran@kokosites.in</>}
                    </button>
                    <button onClick={() => setAiDraft(null)} className="border border-white/10 text-zinc-400 py-3 px-6 text-xs font-bold uppercase tracking-widest hover:border-red-600/50 hover:text-white transition-colors">
                      Regenerate
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="max-w-2xl mx-auto">
                <div className="text-center mb-8">
                  <div className="w-14 h-14 bg-red-600/10 flex items-center justify-center mx-auto mb-4">
                    <Sparkles size={24} className="text-red-500" />
                  </div>
                  <h3 className="text-lg font-black uppercase tracking-tight text-white mb-2">AI Email Composer</h3>
                  <p className="text-xs text-zinc-500">Tell AI what to write — it drafts the email, you review and send.</p>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Recipient Name <span className="text-zinc-700">(optional, for personalization)</span></Label>
                    <Input data-testid="ai-email-recipient" value={aiRecipientName} onChange={e => setAiRecipientName(e.target.value)} placeholder="e.g. Rajesh Kumar" className="bg-transparent border-white/20 rounded-none text-white text-sm focus-visible:ring-0" />
                  </div>

                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">What should the email say? *</Label>
                    <Textarea data-testid="ai-email-prompt" value={aiPrompt} onChange={e => setAiPrompt(e.target.value)} rows={4} placeholder={"e.g.\n• Send quote for 5-page website with animations - Rs 17,000\n• Follow up on unpaid invoice from last week\n• Thank client for payment and share project timeline\n• Ask for testimonial after project delivery"} className="bg-transparent border-white/20 rounded-none text-white text-sm focus-visible:ring-0 resize-none" />
                  </div>

                  <button onClick={handleAiDraft} disabled={aiLoading || !aiPrompt.trim()} data-testid="ai-email-generate" className="w-full bg-red-600 text-white py-3 text-xs font-bold uppercase tracking-widest hover:bg-white hover:text-black transition-all disabled:opacity-50 flex items-center justify-center gap-2">
                    {aiLoading ? <><Loader2 size={14} className="animate-spin" /> Drafting...</> : <><Sparkles size={14} /> Generate Email</>}
                  </button>

                  <div className="border border-white/5 p-4 mt-4">
                    <p className="text-[10px] uppercase tracking-wider text-zinc-600 mb-2">Quick Prompts</p>
                    <div className="flex flex-wrap gap-2">
                      {[
                        "Send website quote with pricing breakdown",
                        "Follow up on pending payment",
                        "Project completed - ask for review",
                        "Share project progress update",
                        "Welcome new client onboarding",
                        "Offer website maintenance plan",
                      ].map(q => (
                        <button key={q} onClick={() => setAiPrompt(q)} className="text-[10px] px-3 py-1.5 border border-white/10 text-zinc-500 hover:text-red-500 hover:border-red-600/30 transition-colors">
                          {q}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="email-health" className="space-y-6">
            <EmailHealthPanel API={API} token={localStorage.getItem("koko_token")} />
          </TabsContent>

          {user?.role === "admin" && (
            <TabsContent value="team" className="space-y-6">
              <TeamPanel API={API} token={localStorage.getItem("koko_token")} currentUser={user} />
            </TabsContent>
          )}
        </Tabs>
      </main>
    </div>
  );
}

function EmailHealthPanel({ API, token }) {
  const [quota, setQuota] = useState(null);
  const [logs, setLogs] = useState([]);
  const [queue, setQueue] = useState({ items: [], pending_count: 0 });
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [flushing, setFlushing] = useState(false);
  const [msg, setMsg] = useState("");

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const refresh = async () => {
    setLoading(true);
    try {
      const [q, l, qu] = await Promise.all([
        axios.get(`${API}/admin/email/quota`, auth),
        axios.get(`${API}/admin/email/logs?limit=50`, auth),
        axios.get(`${API}/admin/email/queue?limit=25`, auth),
      ]);
      setQuota(q.data);
      setLogs(l.data);
      setQueue(qu.data);
    } catch (e) {
      setMsg("Failed to load email data");
    }
    setLoading(false);
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const sendTest = async () => {
    setTesting(true);
    setMsg("");
    try {
      const { data } = await axios.post(`${API}/admin/email/test`, {}, auth);
      if (data.ok) {
        setMsg(`✅ Test email sent via ${data.sender} (id: ${data.id?.slice(0, 8)}…)`);
      } else if (data.queued) {
        setMsg(`⏸ Test queued: ${data.reason}`);
      } else {
        setMsg(`❌ Failed: ${data.error}`);
      }
      await refresh();
    } catch (e) {
      setMsg(`❌ ${e.response?.data?.detail || e.message}`);
    }
    setTesting(false);
  };

  const flushQueueNow = async () => {
    setFlushing(true);
    setMsg("");
    try {
      const { data } = await axios.post(`${API}/admin/email/queue/flush`, {}, auth);
      setMsg(`Flush complete — sent: ${data.sent}, still queued: ${data.still_queued}, failed: ${data.failed}`);
      await refresh();
    } catch (e) {
      setMsg(`❌ ${e.response?.data?.detail || e.message}`);
    }
    setFlushing(false);
  };

  // Banner severity styling
  const level = quota?.level || "ok";
  const bannerStyles = {
    ok: null,
    warning: { bg: "bg-yellow-500/10", border: "border-yellow-500/60", text: "text-yellow-400", icon: "text-yellow-500", label: "QUOTA WARNING" },
    critical: { bg: "bg-red-600/10", border: "border-red-600/70", text: "text-red-400", icon: "text-red-500", label: "QUOTA CRITICAL" },
    exhausted: { bg: "bg-red-900/30", border: "border-red-700", text: "text-red-300", icon: "text-red-600", label: "QUOTA EXHAUSTED" },
  };
  const bs = bannerStyles[level];

  const remainingAccent = (remaining, warning, critical) => {
    if (remaining <= 0) return "text-red-600";
    if (remaining <= critical) return "text-red-500";
    if (remaining <= warning) return "text-yellow-400";
    return "text-green-500";
  };

  return (
    <div className="space-y-6">
      {/* Severity Banner */}
      {bs && (
        <div data-testid="quota-alert-banner" className={`${bs.bg} ${bs.border} border-2 p-4 flex items-start gap-3`}>
          <AlertTriangle size={22} className={`${bs.icon} shrink-0 mt-0.5`} />
          <div className="min-w-0 flex-1">
            <p className={`text-[11px] font-black uppercase tracking-widest ${bs.text}`}>{bs.label}</p>
            {(quota?.alerts || []).map((a, i) => (
              <p key={i} className="text-sm text-white mt-1 leading-relaxed">{a}</p>
            ))}
            <p className="text-[11px] text-zinc-400 mt-2">
              Priority behavior: <span className="text-white">Critical</span> emails (leads, payments) always send. <span className="text-yellow-400">Low-priority</span> emails (review alerts) are queued when quota is critical or exhausted.
            </p>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <StatCard label="Sent Today" value={quota?.sent_today ?? "–"} sub={`/ ${quota?.daily_limit ?? 100}`} />
        <StatCard
          label="Remaining Today"
          value={quota?.remaining_today ?? "–"}
          sub="daily budget left"
          accent={quota ? remainingAccent(quota.remaining_today, quota.thresholds?.daily_warning, quota.thresholds?.daily_critical) : "text-white"}
        />
        <StatCard label="Sent This Month" value={quota?.sent_this_month ?? "–"} sub={`/ ${quota?.monthly_limit ?? 3000}`} />
        <StatCard
          label="Remaining Month"
          value={quota?.remaining_this_month ?? "–"}
          sub="monthly budget left"
          accent={quota ? remainingAccent(quota.remaining_this_month, quota.thresholds?.monthly_warning, quota.thresholds?.monthly_critical) : "text-white"}
        />
        <StatCard
          label="Queued"
          value={quota?.queued ?? 0}
          sub={quota?.queued > 0 ? "awaiting send" : "empty"}
          accent={quota?.queued > 0 ? "text-yellow-400" : "text-white"}
        />
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3 items-center">
        <button
          onClick={sendTest}
          disabled={testing}
          data-testid="send-test-email-btn"
          className="btn-brutal bg-red-600 text-white font-bold uppercase tracking-widest py-3 px-6 border-2 border-white hover:bg-white hover:text-black text-xs flex items-center gap-2 disabled:opacity-50"
        >
          <Send size={14} /> {testing ? "Sending..." : "Send Test Email"}
        </button>
        {queue.pending_count > 0 && (
          <button
            onClick={flushQueueNow}
            disabled={flushing}
            data-testid="flush-queue-btn"
            className="btn-brutal bg-yellow-500 text-black font-bold uppercase tracking-widest py-3 px-6 border-2 border-white hover:bg-white text-xs flex items-center gap-2 disabled:opacity-50"
          >
            <Zap size={14} /> {flushing ? "Flushing..." : `Flush Queue (${queue.pending_count})`}
          </button>
        )}
        <button
          onClick={refresh}
          disabled={loading}
          className="btn-brutal-ghost bg-transparent text-white font-bold uppercase tracking-widest py-3 px-6 border-2 border-white/40 text-xs flex items-center gap-2"
        >
          <RefreshCw size={14} /> Refresh
        </button>
        {msg && <span className="text-xs text-zinc-300">{msg}</span>}
      </div>

      {/* Queue (only when non-empty) */}
      {queue.items.length > 0 && (
        <div className="border border-yellow-500/40 bg-[#0A0A0A]">
          <div className="px-4 py-3 border-b border-yellow-500/30 flex items-center gap-2">
            <Clock size={14} className="text-yellow-500" />
            <h3 className="text-xs font-bold uppercase tracking-widest text-yellow-400">Email Queue</h3>
            <span className="text-[10px] text-zinc-500 ml-auto">Queued items auto-send when quota frees up, or use Flush Queue above</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-black/40">
                <tr className="text-[10px] uppercase tracking-widest text-zinc-500">
                  <th className="text-left p-3">Queued At</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-left p-3">Priority</th>
                  <th className="text-left p-3">To</th>
                  <th className="text-left p-3">Subject</th>
                  <th className="text-left p-3">Reason</th>
                </tr>
              </thead>
              <tbody>
                {queue.items.map((q, i) => (
                  <tr key={i} className="border-t border-white/5">
                    <td className="p-3 text-zinc-500 whitespace-nowrap">{new Date(q.created_at).toLocaleString()}</td>
                    <td className={`p-3 font-bold uppercase ${q.status === "pending" ? "text-yellow-400" : q.status === "sent" ? "text-green-500" : "text-red-500"}`}>{q.status}</td>
                    <td className="p-3 text-zinc-300">{q.priority}</td>
                    <td className="p-3 text-white">{q.to}</td>
                    <td className="p-3 text-zinc-300 truncate max-w-[240px]">{q.subject}</td>
                    <td className="p-3 text-zinc-500 text-[11px] max-w-[260px] truncate">{q.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Log table */}
      <div className="border border-white/10 bg-[#0A0A0A]">
        <div className="px-4 py-3 border-b border-white/10">
          <h3 className="text-xs font-bold uppercase tracking-widest text-white">Recent Send Attempts</h3>
        </div>
        {logs.length === 0 ? (
          <p className="p-8 text-center text-zinc-500 text-sm">No emails logged yet. Send a test above.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-black/40">
                <tr className="text-[10px] uppercase tracking-widest text-zinc-500">
                  <th className="text-left p-3">When</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-left p-3">To</th>
                  <th className="text-left p-3">Subject</th>
                  <th className="text-left p-3">Detail</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((e, i) => {
                  const color =
                    e.status === "sent" ? "text-green-500" :
                    e.status === "fallback_sent" ? "text-yellow-500" :
                    e.status === "queued" ? "text-yellow-400" :
                    "text-red-500";
                  return (
                    <tr key={i} className="border-t border-white/5 hover:bg-white/5">
                      <td className="p-3 text-zinc-500 whitespace-nowrap">{new Date(e.created_at).toLocaleString()}</td>
                      <td className={`p-3 font-bold uppercase ${color}`}>{e.status.replace("_", " ")}</td>
                      <td className="p-3 text-white">{e.to}</td>
                      <td className="p-3 text-zinc-300 truncate max-w-[240px]">{e.subject}</td>
                      <td className="p-3 text-zinc-500 text-[11px]">{e.error ? `err: ${e.error.slice(0, 60)}` : e.id ? `id: ${e.id.slice(0, 8)}…` : e.reason ? `reason: ${e.reason.slice(0, 60)}` : ""}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, small, accent = "text-white" }) {
  return (
    <div className="border border-white/10 bg-[#111] p-4 card-accent card-top-bar">
      <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-2">{label}</p>
      <p className={`${small ? "text-sm" : "text-3xl"} font-black ${accent} truncate`}>{value}</p>
      {sub && <p className="text-[10px] text-zinc-500 mt-1">{sub}</p>}
    </div>
  );
}
